import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { positionsService } from '@/services/positions.service';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Building2,
  TrendingUp,
} from 'lucide-react';
import { cn } from '@caruso/ui';

export function PositionsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['positions', { search, page, active: activeFilter }],
    queryFn: () =>
      positionsService.getAll({
        search,
        page,
        pageSize: 10,
        active: activeFilter,
      }),
  });

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este cargo?')) return;

    try {
      await positionsService.delete(id);
      toast({
        title: 'Cargo excluído',
        variant: 'success',
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Erro ao excluir cargo',
        variant: 'destructive',
      });
    }
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Cargos</h1>
          <p className="text-brand-ivory/60 mt-1">
            Gerencie o cadastro de cargos
          </p>
        </div>
        <Button onClick={() => navigate('/app/cargos/novo')} variant="premium">
          <Plus className="w-4 h-4 mr-2" />
          Nova Posição
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <div className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
            <Input
              placeholder="Buscar por nome, departamento, nível..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={activeFilter === undefined ? 'premium' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter(undefined)}
              className={activeFilter === undefined ? '' : 'border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10'}
            >
              Todos
            </Button>
            <Button
              variant={activeFilter === true ? 'premium' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter(true)}
              className={activeFilter === true ? '' : 'border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10'}
            >
              Ativos
            </Button>
            <Button
              variant={activeFilter === false ? 'premium' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter(false)}
              className={activeFilter === false ? '' : 'border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10'}
            >
              Inativos
            </Button>
          </div>
        </div>
      </Card>

      {/* Positions List */}
      {isLoading ? (
        <div className="text-center py-12 text-brand-ivory/50">
          Carregando...
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {data?.data.map((position) => (
              <motion.div
                key={position.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
              >
                <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all cursor-pointer">
                  <div className="p-6 flex items-center justify-between" onClick={() => navigate(`/app/cargos/${position.id}`)}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-brand-gold" />
                        <h3 className="text-lg font-serif text-brand-gold">
                          {position.name}
                        </h3>
                        {!position.active && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-red-900/50 text-red-400">
                            Inativo
                          </span>
                        )}
                      </div>
                      {position.description && (
                        <p className="mt-2 text-sm text-brand-ivory/60 line-clamp-2">
                          {position.description}
                        </p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-4 text-sm text-brand-ivory/60">
                        {position.department && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {position.department}
                          </span>
                        )}
                        {position.level && (
                          <span className="flex items-center gap-1">
                            <TrendingUp className="w-4 h-4" />
                            {position.level}
                          </span>
                        )}
                        {position.baseSalary && (
                          <span className="flex items-center gap-1 text-brand-gold">
                            {formatCurrency(position.baseSalary)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/app/cargos/${position.id}`)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(position.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-4 text-brand-ivory">
                Página {page} de {data.totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(page + 1)}
                disabled={page === data.totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {data?.data.length === 0 && (
            <div className="text-center py-12">
              <Briefcase className="w-16 h-16 mx-auto text-brand-gold/20 mb-4" />
              <p className="text-brand-ivory/50">Nenhum cargo encontrado</p>
              <Button
                onClick={() => navigate('/app/cargos/novo')}
                variant="outline"
                className="mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar primeiro cargo
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
